import { useEffect, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Save, Settings, Trash2 } from "lucide-react";
import { useToast, useProcessing } from "../context/ui-feedback-context";
import { ESTILO_BOTON_MODAL_ACEPTAR } from "../utils/const/estilos-const";
import { COLOR_AZUL_MAIN } from "../utils/const/colors-const";
import {
  obtenerListaMetaTemplate,
  crearMetaTemplate,
  deshabilitarMetaTemplate,
} from "../api/api-meta-template";

const NOMBRE_MAX_LENGTH = 128;
const DESCRIPCION_MAX_LENGTH = 512;

const stripValues = (campos) =>
  (Array.isArray(campos) ? campos : []).map((c) => ({
    ...c,
    value: "",
  }));

export default function MetaTemplatePicker({
  currentCampos = [],
  onApply,
  onBeforeSave,
}) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { showProcessing, hideProcessing } = useProcessing();

  const [listaTemplates, setListaTemplates] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [selected, setSelected] = useState(null);

  const [guardarModalVisible, setGuardarModalVisible] = useState(false);
  const [guardarForm, setGuardarForm] = useState({
    nombre: "",
    descripcion: "",
    esGlobal: false,
  });

  const [gestionarModalVisible, setGestionarModalVisible] = useState(false);

  const cargarLista = async () => {
    setCargando(true);
    const response = await obtenerListaMetaTemplate();
    setCargando(false);
    if (response.error === false) {
      setListaTemplates(response.data);
    }
  };

  useEffect(() => {
    cargarLista();
  }, []);

  const opciones = listaTemplates.map((tpl) => ({
    value: tpl.id,
    label:
      tpl.idUsuarioWeb == null
        ? `${tpl.nombre} · ${t("label_global") || "Global"}`
        : tpl.nombre,
    template: tpl,
  }));

  const handleAplicar = () => {
    if (!selected) {
      showToast(
        t("alert_debe_seleccionar_template") || "Seleccione un template",
        "warning",
        3000,
      );
      return;
    }
    const campos = stripValues(selected.template.campos);
    if (typeof onApply === "function") {
      onApply(campos);
    }
  };

  const abrirGuardarModal = () => {
    if (typeof onBeforeSave === "function" && onBeforeSave() === false) {
      return;
    }
    if (!Array.isArray(currentCampos) || currentCampos.length === 0) {
      showToast(
        t("alert_no_hay_campos_para_guardar") || "No hay campos para guardar",
        "warning",
        3000,
      );
      return;
    }
    setGuardarForm({ nombre: "", descripcion: "", esGlobal: false });
    setGuardarModalVisible(true);
  };

  const handleGuardar = async () => {
    const nombre = guardarForm.nombre.trim();
    if (nombre.length === 0) {
      showToast(
        t("alert_debe_ingresar_nombre") || "Ingrese un nombre",
        "warning",
        3000,
      );
      return;
    }
    const campos = stripValues(currentCampos);
    showProcessing();
    const response = await crearMetaTemplate({
      nombre,
      descripcion: guardarForm.descripcion.trim() || null,
      campos,
      esGlobal: guardarForm.esGlobal,
    });
    hideProcessing();
    if (response.error === false) {
      showToast(
        t("alert_se_creo_correctamente") || "Template guardado",
        "success",
        3000,
      );
      setGuardarModalVisible(false);
      await cargarLista();
      return;
    }
    showToast(response.message || t("alert_no_se_pudo_crear"), "error", 3000);
  };

  const handleDeshabilitar = async (template) => {
    showProcessing();
    const response = await deshabilitarMetaTemplate(template.id);
    hideProcessing();
    if (response.error === false) {
      showToast(
        t("alert_se_deshabilito_correctamente") || "Template deshabilitado",
        "success",
        3000,
      );
      if (selected?.value === template.id) setSelected(null);
      await cargarLista();
      return;
    }
    showToast(
      response.message ||
        t("alert_no_se_pudo_deshabilitar") ||
        "No se pudo deshabilitar",
      "error",
      3000,
    );
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 40,
      borderColor: state.isFocused ? COLOR_AZUL_MAIN : "#dadce0",
      boxShadow: "none",
      "&:hover": {
        borderColor: state.isFocused ? COLOR_AZUL_MAIN : "#5f6368",
      },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="rounded-lg bg-white border border-gray-200 p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1 min-w-0">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t("label_aplicar_template") || "Aplicar template"}
          </label>
          <Select
            options={opciones}
            value={selected}
            onChange={setSelected}
            isLoading={cargando}
            isClearable
            isSearchable
            placeholder={
              t("placeholder_seleccione_template") || "Seleccione un template"
            }
            styles={selectStyles}
            menuPortalTarget={document.body}
            className="text-black"
          />
        </div>
        <button
          type="button"
          className={ESTILO_BOTON_MODAL_ACEPTAR}
          onClick={handleAplicar}
          disabled={!selected}
        >
          {t("label_aplicar") || "Aplicar"}
        </button>
        <button
          type="button"
          className={ESTILO_BOTON_MODAL_ACEPTAR}
          onClick={abrirGuardarModal}
        >
          <span className="inline-flex items-center gap-1">
            <Save className="h-4 w-4" />
            {t("label_guardar_como_template") || "Guardar como template"}
          </span>
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-3 py-2 rounded text-gray-600 hover:bg-gray-100 border border-gray-300"
          onClick={() => setGestionarModalVisible(true)}
          title={t("label_gestionar_templates") || "Gestionar templates"}
        >
          <Settings className="h-4 w-4" />
        </button>
      </div>

      {guardarModalVisible && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t("label_guardar_como_template") || "Guardar como template"}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("label_nombre")}
                  <span className="ml-1 text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={guardarForm.nombre}
                  onChange={(e) => {
                    if (e.target.value.length <= NOMBRE_MAX_LENGTH) {
                      setGuardarForm({
                        ...guardarForm,
                        nombre: e.target.value,
                      });
                    }
                  }}
                  maxLength={NOMBRE_MAX_LENGTH}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {guardarForm.nombre.length}/{NOMBRE_MAX_LENGTH}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("label_descripcion")}
                </label>
                <textarea
                  value={guardarForm.descripcion}
                  onChange={(e) => {
                    if (e.target.value.length <= DESCRIPCION_MAX_LENGTH) {
                      setGuardarForm({
                        ...guardarForm,
                        descripcion: e.target.value,
                      });
                    }
                  }}
                  rows={3}
                  maxLength={DESCRIPCION_MAX_LENGTH}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {guardarForm.descripcion.length}/{DESCRIPCION_MAX_LENGTH}
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={guardarForm.esGlobal}
                  onChange={(e) =>
                    setGuardarForm({
                      ...guardarForm,
                      esGlobal: e.target.checked,
                    })
                  }
                />
                {t("label_template_global") ||
                  "Disponible para todos los usuarios (global)"}
              </label>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setGuardarModalVisible(false)}
                className="px-4 py-2 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                {t("label_cancelar")}
              </button>
              <button
                type="button"
                onClick={handleGuardar}
                className={ESTILO_BOTON_MODAL_ACEPTAR}
              >
                {t("label_guardar") || "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {gestionarModalVisible && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t("label_gestionar_templates") || "Gestionar templates"}
              </h3>
            </div>
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
              {listaTemplates.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">
                  {t("label_sin_datos")}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {listaTemplates.map((tpl) => (
                    <li key={tpl.id} className="py-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 break-words">
                          {tpl.nombre}
                          {tpl.idUsuarioWeb == null && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                              {t("label_global") || "Global"}
                            </span>
                          )}
                        </p>
                        {tpl.descripcion && (
                          <p className="text-xs text-gray-500 break-words mt-0.5">
                            {tpl.descripcion}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-0.5">
                          {tpl.campos?.length ?? 0}{" "}
                          {t("label_campos") || "campos"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeshabilitar(tpl)}
                        className="p-2 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                        title={t("label_deshabilitar") || "Deshabilitar"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setGestionarModalVisible(false)}
                className="px-4 py-2 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                {t("label_cerrar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
